
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { InteractionFormValues, CandidateInteraction } from '@/components/candidates/types';
// Removed useAuth import as we'll use supabase.auth.getUser()

export const useInteractionSubmit = (candidateId: string, onSuccessCallback?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation<CandidateInteraction, Error, Omit<InteractionFormValues, 'candidate_id'>>({
    mutationFn: async (interactionData: Omit<InteractionFormValues, 'candidate_id'>): Promise<CandidateInteraction> => {
      const { data: { user } } = await supabase.auth.getUser(); // Get user directly from Supabase
      
      if (!user) throw new Error("User not authenticated");
      const currentUserId = user.id;

      const newInteraction = {
        ...interactionData,
        candidate_id: candidateId,
        created_by: currentUserId,
      };

      const { data, error } = await supabase
        .from('candidate_interactions')
        .insert(newInteraction)
        .select()
        .single();

      if (error) {
        console.error('Error creating interaction:', error);
        throw new Error(error.message);
      }
      return data as CandidateInteraction;
    },
    onSuccess: (data) => {
      toast.success('Interaction added successfully!');
      queryClient.invalidateQueries({ queryKey: ['candidateInteractions', candidateId] });
      queryClient.invalidateQueries({ queryKey: ['candidate-interactions', candidateId] });
      queryClient.invalidateQueries({ queryKey: ['candidateActivities', candidateId] });
      if (onSuccessCallback) {
        onSuccessCallback();
      }
    },
    onError: (error) => {
      toast.error(`Failed to add interaction: ${error.message}`);
    },
  });
};
